// Showcase Project Card Component
// Custom web component for displaying showcase project cards

import type { ShowcaseProject, TranslatedText } from "./showcase-config.ts";
import type { ShowcaseKeyOutcome } from "./showcase-key-outcome.ts";
import type { ShowcaseTechBadge } from "./showcase-tech-badge.ts";
import type { ShowcaseMetric } from "./showcase-metric.ts";

class ShowcaseProjectCard extends HTMLElement {
  private config: ShowcaseProject | null = null;
  private hasRendered: boolean = false;

  connectedCallback() {
    const projectId = this.getAttribute("project-id");
    if (!projectId) {
      console.error("ShowcaseProjectCard: project-id attribute is required");
      return;
    }
    // Don't render yet, wait for config
  }

  setConfig(config: ShowcaseProject) {
    this.config = config;
    if (!this.hasRendered) {
      this.render();
      this.hasRendered = true;
    }
  }

  private getCurrentLang(): string {
    if (typeof globalThis.getInitialLang === "function") {
      return globalThis.getInitialLang();
    }
    return "en";
  }

  private translate(text: TranslatedText): string {
    const lang = this.getCurrentLang();
    return text[lang as keyof TranslatedText] || text.en;
  }

  private render() {
    if (!this.config) return;

    // Show image instead of metric if screenshot is available
    const rightColumnHtml = this.config.screenshot
      ? `<div class="md:w-1/3 p-4 flex items-center justify-center bg-slate-50">
           <img src="${this.config.screenshot}" alt="${this.translate(this.config.title)}" class="w-full h-full object-cover rounded-lg shadow-md" />
         </div>`
      : `<div class="md:w-1/3" id="metric-${this.config.id}"></div>`;

    this.innerHTML = `
      <div class="showcase-card bg-white rounded-2xl shadow-lg overflow-hidden mb-12">
        <div class="md:flex">
          <div class="md:w-2/3 p-8 md:p-12">
            <div class="flex items-center gap-3 mb-4">
              <div class="w-12 h-12 bg-gradient-to-br ${this.config.iconGradient} rounded-xl flex items-center justify-center">
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="${this.config.iconSvgPath}"></path>
                </svg>
              </div>
              <div>
                <h3 class="text-2xl font-bold text-slate-900">
                  <span data-en="${this.config.title.en}" data-lv="${this.config.title.lv}" data-it="${this.config.title.it}">
                    ${this.translate(this.config.title)}
                  </span>
                </h3>
                <p class="text-sm text-slate-500">
                  <span data-en="${this.config.industry.en}" data-lv="${this.config.industry.lv}" data-it="${this.config.industry.it}">
                    ${this.translate(this.config.industry)}
                  </span>
                </p>
              </div>
            </div>

            <p class="text-slate-600 mb-6 leading-relaxed">
              <span data-en="${this.config.description.en}" data-lv="${this.config.description.lv}" data-it="${this.config.description.it}">
                ${this.translate(this.config.description)}
              </span>
            </p>

            <div class="mb-6">
              <h4 class="font-bold text-slate-900 mb-3">
                <span data-en="Key Outcomes" data-lv="Galvenie Rezultāti" data-it="Risultati Chiave">Key Outcomes</span>
              </h4>
              <ul class="space-y-2" id="key-outcomes-${this.config.id}">
              </ul>
            </div>

            <div class="flex flex-wrap gap-2" id="tech-badges-${this.config.id}">
            </div>
          </div>
          ${rightColumnHtml}
        </div>
      </div>
    `;

    // Render key outcomes
    setTimeout(() => {
      const outcomesContainer = this.querySelector(`#key-outcomes-${this.config!.id}`);
      if (outcomesContainer) {
        this.config!.keyOutcomes.forEach((outcome) => {
          const outcomeElement = document.createElement("showcase-key-outcome") as ShowcaseKeyOutcome;
          outcomeElement.setConfig(outcome);
          outcomesContainer.appendChild(outcomeElement);
        });
      }

      // Render tech badges
      const badgesContainer = this.querySelector(`#tech-badges-${this.config!.id}`);
      if (badgesContainer) {
        this.config!.techBadges.forEach((badge) => {
          const badgeElement = document.createElement("showcase-tech-badge") as ShowcaseTechBadge;
          badgeElement.setConfig(badge);
          badgesContainer.appendChild(badgeElement);
        });
      }

      // Render metric only if no screenshot is available
      if (!this.config!.screenshot) {
        const metricContainer = this.querySelector(`#metric-${this.config!.id}`);
        if (metricContainer) {
          const metricElement = document.createElement("showcase-metric") as ShowcaseMetric;
          metricElement.setConfig(this.config!.metric);
          metricContainer.appendChild(metricElement);
        }
      }

      // Re-apply language after rendering
      if (globalThis.switchLang) {
        const currentLang = localStorage.getItem("dammi_lang") || "en";
        globalThis.switchLang(currentLang);
      }
    }, 0);
  }
}

customElements.define("showcase-project-card", ShowcaseProjectCard);

export { ShowcaseProjectCard };

